import React, { useEffect, useState, useMemo } from 'react';
import GradientSpinner from './GradientSpinner';
import { subscriptionsAPI } from '../services/api';
import { formatLocalDate } from '../utils/formatLocalDate';
import '../App.css';

/**
 * Themed subscribe flow: pick plan + billing cycle, review, then redirect to Stripe Checkout.
 */
const SubscribeCheckoutModal = ({
  isOpen,
  onClose,
  plans = [],
  initialPlanId = null,
  subscriptionInfo = null,
  returnTab = 'overview'
}) => {
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setSubmitting(false);
    const first = plans[0]?._id || null;
    setSelectedPlanId(initialPlanId || first);
    setBillingCycle('monthly');
  }, [isOpen, initialPlanId, plans]);

  const selectedPlan = useMemo(
    () => plans.find((p) => p._id === selectedPlanId) || null,
    [plans, selectedPlanId]
  );

  useEffect(() => {
    if (selectedPlan && billingCycle === 'yearly' && !(Number(selectedPlan.priceYearly) > 0)) {
      setBillingCycle('monthly');
    }
  }, [selectedPlan, billingCycle]);

  const displayPrice = useMemo(() => {
    if (!selectedPlan) return '—';
    if (billingCycle === 'yearly' && selectedPlan.priceYearly > 0) {
      return `$${selectedPlan.priceYearly}/year`;
    }
    return `$${selectedPlan.priceMonthly ?? 0}/month`;
  }, [selectedPlan, billingCycle]);

  const canProceedToCheckout = Boolean(selectedPlan && plans.length > 0);

  if (!isOpen) return null;

  const handleStripeCheckout = async () => {
    if (!selectedPlan?._id) return;
    setError('');
    setSubmitting(true);
    try {
      const { data } = await subscriptionsAPI.createCheckoutSession({
        planId: selectedPlan._id,
        billingCycle,
        returnTab
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      setError(data?.message || 'Could not start checkout.');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Checkout failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="subscribe-modal-title"
      className="subscribe-checkout-backdrop"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        padding: '16px',
        background: 'rgba(10, 14, 20, 0.78)',
        backdropFilter: 'blur(8px)'
      }}
    >
      <div
        className="subscribe-checkout-panel"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          margin: '0 auto',
          padding: '32px 32px 28px',
          borderRadius: '20px',
          background: 'linear-gradient(145deg, var(--bg-secondary, #1a2332) 0%, var(--bg-tertiary, #131a24) 100%)',
          border: '1px solid rgba(51, 255, 217, 0.28)',
          boxShadow: '0 24px 80px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(51, 255, 217, 0.1) inset'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
          {submitting ? <GradientSpinner size={64} label="Starting checkout" /> : <GradientSpinner size={64} />}
        </div>

        <h2
          id="subscribe-modal-title"
          style={{
            margin: '0 0 8px 0',
            fontSize: '1.35rem',
            fontWeight: 700,
            textAlign: 'center',
            color: 'var(--logo-lime-green)'
          }}
        >
          Subscribe & pay
        </h2>
        <p style={{ margin: '0 0 22px 0', textAlign: 'center', color: 'var(--text-primary)', fontSize: '0.95rem', fontWeight: 600 }}>
          Select a plan, then continue to Stripe’s secure checkout
        </p>

        <div className="subscribe-checkout-grid">
          <div className="subscribe-checkout-col">
            <div style={{ marginBottom: '16px' }}>
              <p className="subscribe-checkout-section-label" style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-tertiary)' }}>
                Payment schedule
              </p>
              <div className="subscribe-checkout-billing">
                {['monthly', 'yearly'].map((c) => {
                  const disabledYearly = c === 'yearly' && selectedPlan && !(Number(selectedPlan.priceYearly) > 0);
                  return (
                    <button
                      key={c}
                      type="button"
                      disabled={submitting || disabledYearly}
                      onClick={() => setBillingCycle(c)}
                      className={billingCycle === c ? 'btn btn-primary' : 'btn btn-outline'}
                      style={{
                        minWidth: 0,
                        padding: '10px',
                        fontSize: '0.875rem',
                        opacity: submitting || disabledYearly ? 0.5 : 1
                      }}
                      title={disabledYearly ? 'No yearly price for this plan' : undefined}
                    >
                      {c === 'monthly' ? 'Monthly' : 'Yearly'}
                    </button>
                  );
                })}
              </div>
            </div>

            <p className="subscribe-checkout-section-label" style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-tertiary)' }}>
              Plan
            </p>
            {plans.length === 0 ? (
              <p style={{ margin: '0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                No subscription plans are available yet. Ask an admin to add plans.
              </p>
            ) : (
              <div className="subscribe-checkout-plans">
                {plans.map((plan) => {
                  const sel = plan._id === selectedPlanId;
                  const price =
                    billingCycle === 'yearly' && plan.priceYearly > 0
                      ? `$${plan.priceYearly}/yr`
                      : `$${plan.priceMonthly ?? 0}/mo`;
                  return (
                    <button
                      key={plan._id}
                      type="button"
                      disabled={submitting}
                      onClick={() => setSelectedPlanId(plan._id)}
                      style={{
                        minWidth: 0,
                        textAlign: 'left',
                        padding: '12px 14px',
                        borderRadius: '12px',
                        border: sel ? '2px solid var(--logo-cyan-teal)' : '1px solid var(--border-color)',
                        background: sel ? 'rgba(51, 255, 217, 0.08)' : 'var(--bg-secondary)',
                        cursor: submitting ? 'default' : 'pointer',
                        color: 'var(--text-primary)'
                      }}
                    >
                      <div style={{ fontWeight: 700, color: sel ? 'var(--logo-cyan-teal)' : 'var(--text-primary)' }}>{plan.name}</div>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: '4px' }}>{price}</div>
                      {plan.trialDays > 0 && (
                        <div style={{ fontSize: '0.75rem', color: 'var(--logo-lime-green)', marginTop: '4px' }}>{plan.trialDays}-day trial on checkout</div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="subscribe-checkout-col">
            {selectedPlan && (
              <div
                className="subscribe-checkout-summary"
                style={{
                  padding: '14px',
                  borderRadius: '12px',
                  background: 'rgba(10, 15, 26, 0.5)',
                  border: '1px solid rgba(181, 255, 51, 0.2)',
                  marginBottom: '14px'
                }}
              >
                <p style={{ margin: '0 0 6px 0', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-tertiary)' }}>
                  Summary
                </p>
                <p style={{ margin: 0, fontWeight: 700, color: 'var(--text-primary)' }}>{selectedPlan.name}</p>
                <p style={{ margin: '6px 0 0 0', fontSize: '1.1rem', fontWeight: 700, color: 'var(--logo-cyan-teal)' }}>{displayPrice}</p>
                <p style={{ margin: '8px 0 0 0', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                  Clinics limit: {selectedPlan.clinicsLimit} · Jobs/mo: {selectedPlan.jobPostingLimit}
                </p>
                {subscriptionInfo?.trialActive && subscriptionInfo?.trialEndsAt && (
                  <p style={{ margin: '10px 0 0 0', fontSize: '0.8125rem', color: 'var(--logo-lime-green)' }}>
                    Trial active until {formatLocalDate(subscriptionInfo.trialEndsAt)}
                    {subscriptionInfo.trialDaysRemaining != null && subscriptionInfo.trialDaysRemaining > 0
                      ? ` · ${subscriptionInfo.trialDaysRemaining} day${subscriptionInfo.trialDaysRemaining === 1 ? '' : 's'} left`
                      : ''}
                  </p>
                )}
              </div>
            )}

            <p className="subscribe-checkout-aside-note" style={{ margin: '0 0 14px 0', fontSize: '0.875rem', lineHeight: 1.55, color: 'var(--text-secondary)' }}>
              Card details are entered on Stripe’s hosted checkout page. If checkout fails, confirm Stripe is configured on the server and this plan has Stripe price IDs.
            </p>

            <div className="subscribe-checkout-dots-row">
              <span className="payments-soon-dot" />
              <span className="payments-soon-dot payments-soon-dot--2" />
              <span className="payments-soon-dot payments-soon-dot--3" />
            </div>
          </div>
        </div>

        <footer className="subscribe-checkout-footer">
          {error && (
            <p style={{ margin: '0 0 12px 0', fontSize: '0.875rem', color: '#f87171', textAlign: 'center', width: '100%' }}>{error}</p>
          )}

          <div className="subscribe-checkout-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={submitting || !canProceedToCheckout}
              onClick={handleStripeCheckout}
              style={{ padding: '14px 16px', fontSize: '1.02rem' }}
            >
              {submitting
                ? 'Opening Stripe…'
                : !canProceedToCheckout
                  ? plans.length === 0
                    ? 'No plans available'
                    : 'Select a plan'
                  : 'Checkout'}
            </button>
            <button type="button" className="btn btn-outline" disabled={submitting} onClick={onClose} style={{ padding: '12px' }}>
              Cancel
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default SubscribeCheckoutModal;
